import { Box, VStack } from '@chakra-ui/react';
import { useEffect } from 'react';
import { FilterSort } from './conversation-filter-sort';
import { ConversationList } from './conversation-list';
import { SidebarDrawer } from './conversation-sidebar-drawer';
import { useConversationsContext } from '@/context/conversation-context';

import { useWhatsAppEvents } from '@/context/whatsapp-events-context';
import { ConversationFilters } from '@/features/conversations/conversations.types';
import { defaultConversationFilters } from './conversation-utils';

interface ConversationSidebarProps {
  selectedConversationId?: string;
  onSelectConversation: (conversationId: string) => void;
  isMobile?: boolean;
  isDrawerOpen?: boolean;
  onDrawerClose?: () => void;
}

function ConversationSidebar({
  selectedConversationId,
  onSelectConversation,
  isMobile = false,
  isDrawerOpen = false,
  onDrawerClose,
}: ConversationSidebarProps) {
  // Use our context instead of the hook - SINGLE source of truth
  const {
    conversations,
    loading,
    error,
    total,
    page,
    setPage,
    perPage,
    setSort,
    sort,
    filters,
    setFilters,
    setSelectedConversationId,
    refreshConversations,
  } = useConversationsContext();

  const handleChangePage = (page: number) => {
    setPage(page);
  };

  const { setSelectedConversationIdForContext } = useWhatsAppEvents();

  // Update the selected conversation in the hook and context when it changes via props
  useEffect(() => {
    if (selectedConversationId) {
      setSelectedConversationId(selectedConversationId);
      setSelectedConversationIdForContext(selectedConversationId);
    } else {
      setSelectedConversationIdForContext(null);
    }
  }, [
    selectedConversationId,
    setSelectedConversationId,
    setSelectedConversationIdForContext,
  ]);

  // Handler for sort change
  const handleSortChange = (field: string, order: 'ASC' | 'DESC') => {
    setSort({ field, order });
  };

  // Handler for filter change
  const handleFilterChange = (newFilters: Partial<ConversationFilters>) => {
    setFilters({ ...filters, ...newFilters });
  };

  // Handle new conversation creation
  const handleConversationCreated = (conversationId: string) => {
    refreshConversations();
    onSelectConversation(conversationId);
    if (isMobile && onDrawerClose) {
      onDrawerClose();
    }
  };

  // Content of the sidebar that's shared between desktop and mobile views
  const SidebarContent = () => (
    <VStack
      gap={0}
      align="stretch"
      h="100%"
      borderRight="1px"
      borderColor="gray.200"
      bgColor="white"
    >
      <Box
        px={0}
        py={4}
        borderBottom="1px"
        borderColor="gray.200"
        flexShrink={0}
      >
        <FilterSort
          onSortChange={handleSortChange}
          onFilterChange={handleFilterChange}
          currentFilters={filters}
          onConversationCreated={handleConversationCreated}
          sortOrder={sort.order || 'DESC'}
          sortField={sort.field || 'last_message_at'}
        />
      </Box>
      <Box flex="1" overflowY="auto" overflowX="hidden">
        <ConversationList
          conversations={conversations}
          loading={loading}
          error={error}
          total={total}
          page={page}
          setPage={handleChangePage}
          perPage={perPage}
          selectedConversationId={selectedConversationId}
          onSelectConversation={(conversationId) => {
            onSelectConversation(conversationId);
            setSelectedConversationIdForContext(conversationId);
            if (isMobile && onDrawerClose) {
              onDrawerClose();
            }
          }}
        />
      </Box>
    </VStack>
  );

  // If it's mobile, use the drawer, otherwise show directly
  if (isMobile) {
    return (
      <SidebarDrawer isOpen={isDrawerOpen} onClose={onDrawerClose}>
        <SidebarContent />
      </SidebarDrawer>
    );
  }

  return (
    <Box
      width="360px"
      minWidth="360px"
      maxWidth="360px"
      height="100%"
      borderRight="1px"
      borderColor="gray.200"
      flexShrink={0}
    >
      <SidebarContent />
    </Box>
  );
}

export default ConversationSidebar;
